import { ChangeDetectorRef, Component, Inject, ViewChild, inject } from '@angular/core';
import { formatDate } from '@angular/common';

import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ResultCreerUtilisateurFictifModel } from 'src/models/pilotage/resultCreerUtilisateurFictifModel';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { TiretPipe } from 'src/app/pipe/tiret.pipe';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { ExcelExportService } from '../../excel-export.service';

@Component({
    selector: 'app-result-creer-utilisateurs-fictifs-dialog',
    templateUrl: './result-creer-utilisateurs-fictifs-dialog.component.html',
    styleUrls: ['./result-creer-utilisateurs-fictifs-dialog.component.scss'],
    imports: [MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, ClipboardModule, TranslateModule, TiretPipe, MatPaginatorModule, MatTableModule, MatSortModule],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ]
})
export class ResultCreerUtilisateursFictifsDialogComponent {
  displayedColumns: string[] = [
    'organisation',
    'groupe',
    'prenomNom',
    'titre',
    'actions'
  ];

  pageIndex: number = 0;
  pageSize: number = 10;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  dataSource!: MatTableDataSource<ResultCreerUtilisateurFictifModel>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private readonly excelExportService = inject(ExcelExportService);
  private readonly formatHorodatageFichier = 'yyyy-MM-dd_HHmm';
  private readonly localeHorodatageFichier = 'en-US';

  constructor(@Inject(MAT_DIALOG_DATA) public data: ResultCreerUtilisateurFictifModel[],
    private changeDetectorRef: ChangeDetectorRef,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private translate: TranslateService) {
    this.dataSource = new MatTableDataSource<ResultCreerUtilisateurFictifModel>(this.data);
    this.totalItems = this.data.length;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.changeDetectorRef.detectChanges();
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
    }, 100);
  }

  copyCredentials(utilisateur: ResultCreerUtilisateurFictifModel): void {
    const emailLabel = this.translate.instant('USERS_MANAGEMENT.EMAIL');
    const passwordLabel = this.translate.instant('USERS_MANAGEMENT.PASSWORD');
    const credentials = `${emailLabel}: ${utilisateur.courriel}\n${passwordLabel}: ${utilisateur.password}`;
    const success = this.clipboard.copy(credentials);

    if (success) {
      this.snackBar.open(
        this.translate.instant('USERS_MANAGEMENT.CREDENTIALS_COPIED'),
        this.translate.instant('COMMUN.CLOSE'),
        { duration: 3000 }
      );

      return;
    }

    this.snackBar.open(
      this.translate.instant('USERS_MANAGEMENT.COPY_ERROR'),
      this.translate.instant('COMMUN.CLOSE'),
      { duration: 3000 }
    );
  }

  exporterIdentifiants(): void {
    const enTetePrenom = this.translate.instant('USERS_MANAGEMENT.FIRST_NAME');
    const enTeteNom = this.translate.instant('USERS_MANAGEMENT.LAST_NAME');
    const enTeteCourriel = this.translate.instant('USERS_MANAGEMENT.EMAIL');
    const enTeteMotDePasse = this.translate.instant('USERS_MANAGEMENT.PASSWORD');
    const lignes = this.data.map((d) => ({
      [enTetePrenom]: d.prenom,
      [enTeteNom]: d.nom,
      [enTeteCourriel]: d.courriel,
      [enTeteMotDePasse]: d.password
    }));

    const prefixeFichier = this.translate.instant('USERS_MANAGEMENT.FICTITIOUS_USERS_EXPORT_FILE_NAME');
    const maintenant = new Date();
    const horodatage = formatDate(maintenant, this.formatHorodatageFichier, this.localeHorodatageFichier);
    const nomFichier = `${prefixeFichier}_${horodatage}.xlsx`;

    this.excelExportService.downloadExcel(lignes, nomFichier, prefixeFichier);
  }

  sortDataChange(sortState: Sort) {
    if (sortState.direction) {
      this.dataSource.sort = this.sort;
    }
  }
}
